"use client";

import Image from "next/image";
import Iconify from "@/components/ui/Iconify";
import { getMediaRecordUrl } from "@/utils/media";

/**
 * 왼쪽 목록의 "동네" 판.
 *
 * 지도가 동네를 말하고 있을 때(확대 16.5 미만) 목록도 동네를 말한다. 예전에는 이때도
 * "내 옆 71m 카페" 를 보여줬는데, 지도가 묻는 것과 다른 질문이라 아무도 보지 않았고
 * 조회에 1초 넘게 걸렸다(2026-08-07 사장님 지적).
 *
 * 여기 쓰는 값은 **지도가 이름표를 받을 때 같이 온 것**이다. 추가 조회가 없다.
 */

const AreaRow = ({ area, onSelect }) => {
  // ⚠️ 썸네일 경로만 주면 안 읽는다 — getMediaRecordPath 는 옵션이 없으면
  // filePath/mediaPath/url 만 본다. 썸네일로 읽으라고 알려 준다.
  const cover = area.img
    ? getMediaRecordUrl({ thumbnailPath: area.img }, { variant: "thumbnail" })
    : null;

  return (
    <button
      type="button"
      onClick={() => onSelect(area)}
      className="flex w-full items-center gap-3 border-b border-black/5 px-4 py-3 text-left transition-colors hover:bg-black/[.03] dark:border-white/10 dark:hover:bg-white/5"
    >
      {/* 목록 타일은 세로로 긴 직사각형이다(공용 규격 h-[85px] w-16). 정사각형을 쓰면
          다른 목록들과 어긋난다 — 2026-08-07 사장님 지적. */}
      <div className="relative h-[85px] w-16 shrink-0 overflow-hidden rounded-card bg-black/5 dark:bg-white/10">
        {cover ? (
          <Image
            src={cover}
            alt=""
            fill
            sizes="64px"
            className="object-cover"
            unoptimized
          />
        ) : (
          <span className="flex size-full items-center justify-center text-black/20 dark:text-white/20">
            <Iconify icon="mdi:map-marker-outline" className="size-6" />
          </span>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="truncate text-[15px] font-bold">{area.n}</div>
        {area.s && (
          <div className="mt-0.5 truncate text-xs text-black/50 dark:text-white/50">
            {area.s}
          </div>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-1 text-red">
        <span className="text-sm font-extrabold tabular-nums">
          {Number(area.c || 0).toLocaleString()}
        </span>
        <span className="text-xs font-bold">곳</span>
      </div>
    </button>
  );
};

const AreaList = ({ areas = [], level, onSelect }) => (
  <div className="flex h-full min-h-0 flex-col">
    <div className="flex items-baseline justify-between px-4 py-3">
      <h2 className="text-[15px] font-extrabold">
        {level === "group" ? "어느 동네로 갈까요" : "이 안에서 어디를 볼까요"}
      </h2>
      <span className="text-xs text-black/45 dark:text-white/45">
        {areas.length}곳
      </span>
    </div>

    <div className="min-h-0 flex-1 overflow-y-auto">
      {areas.map((area) => (
        <AreaRow key={area.id || `${area.lat},${area.lng}`} area={area} onSelect={onSelect} />
      ))}
      {!areas.length && (
        <p className="px-4 py-10 text-center text-sm text-black/40 dark:text-white/40">
          이 화면에는 아직 정리된 동네가 없습니다. 지도를 확대하면 가게가 보입니다.
        </p>
      )}
    </div>

    <p className="border-t border-black/5 px-4 py-2.5 text-[11.5px] text-black/40 dark:border-white/10 dark:text-white/40">
      동네를 누르면 그 안으로 들어갑니다. 더 확대하면 가게가 보입니다.
    </p>
  </div>
);

export default AreaList;
